import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  type Classroom,
  type ClassroomMember,
  type JoinRequest,
} from '#/state/classrooms/types'
import {STALE} from '#/state/queries'
import {useAgent} from '#/state/session'
import {classroomProcedure, classroomQuery} from './client'
import {ClassroomApiError} from './errors'
import {classroomKeys, invalidateClassroom} from './keys'
import {
  type ClassroomViewJson,
  type MemberViewJson,
  type RequestViewJson,
  toMember,
  toRequest,
} from './map'
import {cacheClassroomView} from './rooms'

const NS = 'app.taderok.classroom'

export type ClassroomMembersPage = {
  members: ClassroomMember[]
  cursor?: string
}

export function useClassroomMembersQuery(
  id: string | undefined,
  role?: 'teacher' | 'student' | 'parent',
) {
  const agent = useAgent()
  return useInfiniteQuery({
    queryKey: classroomKeys.members(id ?? '', role),
    queryFn: async ({pageParam}): Promise<ClassroomMembersPage> => {
      const res = await classroomQuery<{
        members: MemberViewJson[]
        cursor?: string
      }>(agent, `${NS}.listMembers`, {id, role, cursor: pageParam})
      return {members: res.members.map(toMember), cursor: res.cursor}
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: page => page.cursor,
    staleTime: STALE.SECONDS.THIRTY,
    enabled: !!id,
  })
}

export function useClassroomRequestsQuery(
  id: string | undefined,
  enabled: boolean,
) {
  const agent = useAgent()
  return useQuery<JoinRequest[]>({
    queryKey: classroomKeys.requests(id ?? ''),
    queryFn: async () => {
      const res = await classroomQuery<{requests: RequestViewJson[]}>(
        agent,
        `${NS}.listRequests`,
        {id},
      )
      return res.requests.map(toRequest)
    },
    staleTime: STALE.SECONDS.FIFTEEN,
    // Polling until notifications replace it in Phase 2
    refetchInterval: enabled ? 30_000 : false,
    enabled: !!id && enabled,
  })
}

export function useJoinClassroomMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<
    {status: 'joined' | 'requested' | 'already-member'; classroom: Classroom},
    ClassroomApiError,
    {code?: string; message?: string}
  >({
    mutationFn: async ({code, message}) => {
      const res = await classroomProcedure<{
        status: 'joined' | 'requested' | 'already-member'
        classroom: ClassroomViewJson
      }>(agent, `${NS}.joinClassroom`, {id, code, message})
      return {
        status: res.status,
        classroom: cacheClassroomView(queryClient, res.classroom),
      }
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

/**
 * What answering or withdrawing an ask came to. `alreadyHandled` is the
 * server's `NoRequest`: somebody else got there first, which is the outcome
 * the caller wanted, but the person should be told rather than left guessing.
 */
export type RequestOutcome = {alreadyHandled: boolean}

async function unlessNoRequest(send: () => Promise<unknown>) {
  try {
    await send()
    return {alreadyHandled: false}
  } catch (e) {
    if (e instanceof ClassroomApiError && e.code === 'NoRequest') {
      return {alreadyHandled: true}
    }
    throw e
  }
}

export function useCancelRequestMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<RequestOutcome, ClassroomApiError, void>({
    mutationFn: () =>
      unlessNoRequest(() =>
        classroomProcedure(agent, `${NS}.cancelRequest`, {id}),
      ),
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

export function useRespondToRequestMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<
    RequestOutcome,
    ClassroomApiError,
    {did: string; approve: boolean}
  >({
    mutationFn: ({did, approve}) =>
      unlessNoRequest(() =>
        classroomProcedure(agent, `${NS}.respondToRequest`, {
          id,
          did,
          approve,
        }),
      ),
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

export function useLeaveClassroomMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<void, ClassroomApiError, void>({
    mutationFn: async () => {
      await classroomProcedure(agent, `${NS}.leaveClassroom`, {id})
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}

export function useRemoveMemberMutation(id: string) {
  const agent = useAgent()
  const queryClient = useQueryClient()
  return useMutation<void, ClassroomApiError, {did: string}>({
    mutationFn: async ({did}) => {
      await classroomProcedure(agent, `${NS}.removeMember`, {id, did})
    },
    onSuccess: () => invalidateClassroom(queryClient, id),
  })
}
